import { createFileRoute, Navigate, redirect } from '@tanstack/react-router';
import { useLayoutEffect } from 'react';
import { z } from 'zod';

import { CrmCustomerDirectoryPage, CrmPrimaryNavigation } from '@pages/crm';
import { useAuth } from '@features/auth';
import { useAbility } from '@features/authorization';
import {
  clearCrmCustomerDirectoryProfileReturnMarker,
  crmCustomerDirectoryMaxPage,
  crmCustomerDirectorySearchArraySchema,
  getUserProfilesDirectoryReturnSearch,
  hasCrmCustomerDirectoryProfileReturnMarker,
  markCrmCustomerDirectoryProfileReturn,
  userProfilesDirectorySortSchema,
  userProfilesDirectoryViewSchema,
  type UserProfilesDirectoryView,
} from '@features/crm-customer-directory';
import type { RouterContext } from '@shared/config/router-context';
import {
  canAccessHelpdesk,
  canAccessCrmCustomerDirectory,
  canAccessCrmCustomerMembershipDirectory,
  canAccessCrmCustomerProfile,
} from '@shared/lib';

const CRM_DIRECTORY_RETURN_SCROLL_KEY = 'crm.customerDirectory.returnScroll.v1';

export function shouldReplaceDirectoryNavigation(
  currentView: UserProfilesDirectoryView | undefined,
  nextView: UserProfilesDirectoryView | undefined
): boolean {
  return currentView === nextView;
}
const USER_PROFILES_DIRECTORY_ARRAY_SEARCH_KEYS = [
  'businessLineKeys',
  'certificationTypeIds',
  'cities',
  'currentMembershipStatuses',
  'freshnessStates',
  'linkedSystems',
  'membershipTypeIds',
  'profileOrigins',
  'roles',
] as const;

function normalizeArraySearchValues(search: URLSearchParams): void {
  for (const key of USER_PROFILES_DIRECTORY_ARRAY_SEARCH_KEYS) {
    const normalizedValues = search.getAll(key).flatMap((value) => {
      try {
        const parsed = JSON.parse(value) as unknown;
        return Array.isArray(parsed) && parsed.every((item) => typeof item === 'string')
          ? parsed
          : [value];
      } catch {
        return [value];
      }
    });
    search.delete(key);
    for (const value of normalizedValues) search.append(key, value);
  }
}

function getCanonicalDirectoryLocation(): string {
  const search = new URLSearchParams(window.location.search);
  const rawQuery = search.get('q');
  if (rawQuery !== null) {
    const normalizedQuery = rawQuery.trim();
    if (normalizedQuery.length > 0) search.set('q', normalizedQuery);
    else search.delete('q');
  }
  normalizeArraySearchValues(search);
  search.sort();
  const canonicalSearch = search.toString();
  return `${window.location.pathname}${canonicalSearch.length > 0 ? `?${canonicalSearch}` : ''}`;
}

function rememberDirectoryScrollPosition() {
  window.sessionStorage.setItem(
    CRM_DIRECTORY_RETURN_SCROLL_KEY,
    JSON.stringify({
      location: getCanonicalDirectoryLocation(),
      scrollY: window.scrollY,
    })
  );
}

function restoreDirectoryScrollPosition() {
  const stored = window.sessionStorage.getItem(CRM_DIRECTORY_RETURN_SCROLL_KEY);
  if (stored === null) return;
  window.sessionStorage.removeItem(CRM_DIRECTORY_RETURN_SCROLL_KEY);

  try {
    const parsed = JSON.parse(stored) as { location?: unknown; scrollY?: unknown };
    const currentLocation = getCanonicalDirectoryLocation();
    if (
      parsed.location !== currentLocation ||
      typeof parsed.scrollY !== 'number' ||
      !Number.isFinite(parsed.scrollY)
    ) {
      return;
    }
    window.requestAnimationFrame(() => {
      window.scrollTo({ behavior: 'instant', top: Math.max(0, parsed.scrollY as number) });
    });
  } catch {
    // Invalid transient navigation state is discarded.
  }
}

function hasMatchingDirectoryReturnState(): boolean {
  if (!hasCrmCustomerDirectoryProfileReturnMarker()) return false;
  const stored = window.sessionStorage.getItem(CRM_DIRECTORY_RETURN_SCROLL_KEY);
  if (stored === null) return false;

  try {
    const parsed = JSON.parse(stored) as { location?: unknown };
    return parsed.location === getCanonicalDirectoryLocation();
  } catch {
    return false;
  }
}

export const Route = createFileRoute('/$locale/_authenticated/crm/')({
  validateSearch: z.object({
    accountLinkage: z.enum(['with', 'without']).optional(),
    page: z.coerce.number().int().positive().max(crmCustomerDirectoryMaxPage).optional(),
    q: z.string().trim().max(200).optional(),
    shopifyCustomerId: z.string().trim().max(200).optional(),
    businessLineKeys: crmCustomerDirectorySearchArraySchema(z.string().trim().min(1).max(120)),
    certificationTypeIds: crmCustomerDirectorySearchArraySchema(z.uuid()),
    cities: crmCustomerDirectorySearchArraySchema(z.string().trim().min(1).max(120)),
    currentMembershipStatuses: crmCustomerDirectorySearchArraySchema(
      z.string().trim().min(1).max(120)
    ),
    freshnessStates: crmCustomerDirectorySearchArraySchema(z.enum(['fresh', 'stale', 'unknown'])),
    linkedSystems: crmCustomerDirectorySearchArraySchema(z.enum(['bubble', 'move'])),
    membershipTypeIds: crmCustomerDirectorySearchArraySchema(z.uuid()),
    profileOrigins: crmCustomerDirectorySearchArraySchema(z.enum(['bubble', 'move', 'bizagi'])),
    roles: crmCustomerDirectorySearchArraySchema(z.string().trim().min(1).max(120)),
    sort: userProfilesDirectorySortSchema.optional(),
    view: userProfilesDirectoryViewSchema.optional(),
  }),
  beforeLoad: ({ context, params, search }) => {
    const { auth, ability } = context as RouterContext;

    if (auth.isLoading || !auth.isAuthenticated) {
      return;
    }

    if (!canAccessCrmCustomerDirectory(ability)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/$locale/unauthorized',
        params: { locale: params.locale },
      });
    }
    if (search.view === 'membership') {
      // Retain independently selected filters/search and compatible ordering.
      // Replacing the entry also makes old Back/Forward links safe.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/$locale/crm',
        params: { locale: params.locale },
        search: { ...search, view: undefined },
        replace: true,
      });
    }
  },
  component: CrmCustomerDirectoryRoute,
});

function CrmCustomerDirectoryRoute() {
  const { locale: rawLocale } = Route.useParams();
  const locale = rawLocale as unknown as string;
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const ability = useAbility();
  const { permissions, user } = useAuth();
  const viewerAccessScope = user ? `${user.id}:${JSON.stringify(permissions ?? [])}` : '';
  const shouldRestoreScroll = hasMatchingDirectoryReturnState();

  useLayoutEffect(() => {
    // beforeLoad can run while authentication is still bootstrapping.
    if (search.view === 'membership' && canAccessCrmCustomerDirectory(ability)) {
      void navigate({ replace: true, search: { ...search, view: undefined } });
    }
  }, [ability, navigate, search]);

  useLayoutEffect(() => {
    restoreDirectoryScrollPosition();
    clearCrmCustomerDirectoryProfileReturnMarker();
  }, [shouldRestoreScroll]);

  if (!canAccessCrmCustomerDirectory(ability)) {
    return <Navigate to="/$locale/unauthorized" params={{ locale }} replace />;
  }

  return (
    <main className="min-w-0 p-6">
      <CrmPrimaryNavigation
        canAccessHelpdesk={canAccessHelpdesk(ability)}
        activeSection="profiles"
        canAccessMemberships={canAccessCrmCustomerMembershipDirectory(ability)}
        canAccessProfiles={canAccessCrmCustomerDirectory(ability)}
        locale={locale}
      />
      <CrmCustomerDirectoryPage
        canFilterByBusinessLine={
          ability.can('filterByAffiliation', 'CrmCustomerDirectory') &&
          ability.can('read', 'CrmBusinessLineRegistry') &&
          ability.can('read', 'CrmCustomerAffiliation')
        }
        canFilterByIdentitySource={ability.can('filterByIdentitySource', 'CrmCustomerDirectory')}
        canReadCertificationTypes={ability.can('read', 'CrmCustomerLearning')}
        canReadDocumentMetadata={ability.can('read', 'CrmCustomerDocumentMetadata')}
        canReadMembership={canAccessCrmCustomerMembershipDirectory(ability)}
        canReadSensitiveFields={ability.can('read', 'CrmCustomerProfileSensitive')}
        viewerAccessScope={viewerAccessScope}
        searchState={{
          accountLinkage: search.accountLinkage,
          page: search.page,
          businessLineKeys: search.businessLineKeys,
          certificationTypeIds: search.certificationTypeIds,
          cities: search.cities,
          currentMembershipStatuses: search.currentMembershipStatuses,
          freshnessStates: search.freshnessStates,
          linkedSystems: search.linkedSystems,
          membershipTypeIds: search.membershipTypeIds,
          profileOrigins: search.profileOrigins,
          q: search.q,
          roles: search.roles,
          shopifyCustomerId: search.shopifyCustomerId,
          sort: search.sort,
          view: search.view,
        }}
        onViewStateChange={(next) => {
          void navigate({
            replace: shouldReplaceDirectoryNavigation(search.view, next.view),
            search: getUserProfilesDirectoryReturnSearch({
              accountLinkage: next.accountLinkage,
              businessLineKeys: next.businessLineKeys,
              certificationTypeIds: next.certificationTypeIds,
              cities: next.cities,
              currentMembershipStatuses: next.currentMembershipStatuses,
              freshnessStates: next.freshnessStates,
              linkedSystems: next.linkedSystems,
              membershipTypeIds: next.membershipTypeIds,
              page: next.page === 1 ? undefined : next.page,
              profileOrigins: next.profileOrigins,
              q: next.q,
              roles: next.roles,
              shopifyCustomerId: next.shopifyCustomerId,
              sort: next.sort,
              view: next.view,
            }),
          });
        }}
        onOpenProfile={
          canAccessCrmCustomerProfile(ability)
            ? (customerId, profileArea) => {
                rememberDirectoryScrollPosition();
                markCrmCustomerDirectoryProfileReturn();

                const navigation = {
                  params: { userProfileId: customerId, locale },
                  search: getUserProfilesDirectoryReturnSearch(search),
                };

                if (profileArea === 'membership') {
                  void navigate({
                    to: '/$locale/crm/user-profiles/$userProfileId/membership',
                    ...navigation,
                  });
                  return;
                }

                if (profileArea === 'documents') {
                  void navigate({
                    to: '/$locale/crm/user-profiles/$userProfileId/documents',
                    ...navigation,
                  });
                  return;
                }

                void navigate({ to: '/$locale/crm/user-profiles/$userProfileId', ...navigation });
              }
            : undefined
        }
      />
    </main>
  );
}
